// Per-chain address format validator for the admin Crypto Withdraw Manager.
// Mirrors paraacrypto-frontend/src/utils/addressValidation.ts (itself ported
// from the mobile app) so admin-added whitelist addresses are checked with
// the same rigor as the user-facing withdraw flow — format only
// (prefix/length/charset), no checksum.

const EVM = {
  pattern: /^0x[0-9a-fA-F]{40}$/,
  label: "EVM (0x + 40 hex chars)",
};

const CHAIN_MAP = {
  // ── EVM-compatible ──────────────────────────────────────────────────────────
  erc20: EVM,
  erc721: EVM,
  bep20: EVM,
  bep2: { pattern: /^bnb1[a-z0-9]{38}$/, label: "BNB Beacon Chain (bnb1...)" },
  // BNB Smart Chain (BSC) — modern "BNB" withdrawals are EVM/BEP-20, not the
  // legacy Beacon Chain (bnb1...) format above. Real seeded networkKeys for
  // this network look like "bnb_bsc" — see the lookup() note below for why
  // both "bnb" and "bsc" need to be registered explicitly.
  bnb: EVM,
  bsc: EVM,
  bnbsmartchain: EVM,
  binancesmartchain: EVM,
  binancecoin: EVM,
  polygon: EVM,
  matic: EVM,
  avax: EVM,
  avalanche: EVM,
  arbitrum: EVM,
  optimism: EVM,
  base: EVM,
  ftm: EVM,
  fantom: EVM,
  cro: EVM,
  cronos: EVM,
  eth: EVM,
  ethereum: EVM,

  // ── Bitcoin family ───────────────────────────────────────────────────────────
  // Legacy (P2PKH, starts 1) / Nested SegWit (P2SH, starts 3): 26-35 chars
  // total. Native SegWit v0 (bc1q): 42 (P2WPKH) or 62 (P2WSH) chars. Taproot
  // (bc1p, SegWit v1): always exactly 62 chars.
  btc: {
    pattern: /^(1|3)[a-zA-HJ-NP-Z0-9]{25,34}$|^bc1q[a-z0-9]{38}$|^bc1q[a-z0-9]{58}$|^bc1p[a-z0-9]{58}$/,
    label: "Bitcoin (1…, 3…, bc1q…, or bc1p…)",
  },
  bitcoin: {
    pattern: /^(1|3)[a-zA-HJ-NP-Z0-9]{25,34}$|^bc1q[a-z0-9]{38}$|^bc1q[a-z0-9]{58}$|^bc1p[a-z0-9]{58}$/,
    label: "Bitcoin (1…, 3…, bc1q…, or bc1p…)",
  },
  ltc: {
    pattern: /^([LM][a-km-zA-HJ-NP-Z1-9]{26,33}|ltc1[a-z0-9]{6,87})$/,
    label: "Litecoin (L…, M…, or ltc1…)",
  },
  litecoin: {
    pattern: /^([LM][a-km-zA-HJ-NP-Z1-9]{26,33}|ltc1[a-z0-9]{6,87})$/,
    label: "Litecoin (L…, M…, or ltc1…)",
  },
  doge: {
    pattern: /^D[5-9A-HJ-NP-Za-km-z][1-9A-HJ-NP-Za-km-z]{32}$/,
    label: "Dogecoin (D…)",
  },
  dogecoin: {
    pattern: /^D[5-9A-HJ-NP-Za-km-z][1-9A-HJ-NP-Za-km-z]{32}$/,
    label: "Dogecoin (D…)",
  },
  bch: {
    pattern: /^(bitcoincash:)?(q|p)[a-z0-9]{41}$|^[13][a-zA-HJ-NP-Z0-9]{25,34}$/,
    label: "Bitcoin Cash (q…, p…, 1…, or 3…)",
  },
  bitcoincash: {
    pattern: /^(bitcoincash:)?(q|p)[a-z0-9]{41}$|^[13][a-zA-HJ-NP-Z0-9]{25,34}$/,
    label: "Bitcoin Cash",
  },

  // ── Tron ─────────────────────────────────────────────────────────────────────
  trc20: { pattern: /^T[a-zA-HJ-NP-Z0-9]{33}$/, label: "Tron (T…)" },
  trc10: { pattern: /^T[a-zA-HJ-NP-Z0-9]{33}$/, label: "Tron (T…)" },
  trx: { pattern: /^T[a-zA-HJ-NP-Z0-9]{33}$/, label: "Tron (T…)" },
  tron: { pattern: /^T[a-zA-HJ-NP-Z0-9]{33}$/, label: "Tron (T…)" },

  // ── Solana ───────────────────────────────────────────────────────────────────
  sol: { pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/, label: "Solana (base58, 32–44 chars)" },
  solana: { pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/, label: "Solana (base58, 32–44 chars)" },
  spl: { pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/, label: "Solana SPL (base58, 32–44 chars)" },

  // ── XRP / Ripple ─────────────────────────────────────────────────────────────
  xrp: { pattern: /^r[a-zA-HJ-NP-Z0-9]{24,34}$/, label: "XRP (r…)" },
  ripple: { pattern: /^r[a-zA-HJ-NP-Z0-9]{24,34}$/, label: "XRP (r…)" },

  // ── Stellar ──────────────────────────────────────────────────────────────────
  xlm: { pattern: /^G[A-Z2-7]{55}$/, label: "Stellar (G…)" },
  stellar: { pattern: /^G[A-Z2-7]{55}$/, label: "Stellar (G…)" },

  // ── Cardano ──────────────────────────────────────────────────────────────────
  ada: {
    pattern: /^(addr1[a-z0-9]+|Ae2[a-zA-HJ-NP-Z0-9]+|DdzFF[a-zA-HJ-NP-Z0-9]+)$/,
    label: "Cardano (addr1…)",
  },
  cardano: {
    pattern: /^(addr1[a-z0-9]+|Ae2[a-zA-HJ-NP-Z0-9]+|DdzFF[a-zA-HJ-NP-Z0-9]+)$/,
    label: "Cardano (addr1…)",
  },

  // ── Cosmos ecosystem ─────────────────────────────────────────────────────────
  atom: { pattern: /^cosmos1[a-z0-9]{38}$/, label: "Cosmos (cosmos1…)" },
  cosmos: { pattern: /^cosmos1[a-z0-9]{38}$/, label: "Cosmos (cosmos1…)" },
  osmo: { pattern: /^osmo1[a-z0-9]{38}$/, label: "Osmosis (osmo1…)" },

  // ── TON ──────────────────────────────────────────────────────────────────────
  ton: { pattern: /^(EQ|UQ)[a-zA-Z0-9_-]{46}$/, label: "TON (EQ… or UQ…)" },

  // ── Polkadot / Kusama ────────────────────────────────────────────────────────
  dot: { pattern: /^1[1-9A-HJ-NP-Za-km-z]{46,47}$/, label: "Polkadot (1…)" },
  polkadot: { pattern: /^1[1-9A-HJ-NP-Za-km-z]{46,47}$/, label: "Polkadot (1…)" },
  ksm: {
    pattern: /^[CDEFGHJKLMNPQRSTUVWXY][a-zA-HJ-NP-Z0-9]{47}$/,
    label: "Kusama",
  },

  // ── Monero ───────────────────────────────────────────────────────────────────
  xmr: { pattern: /^4[0-9AB][1-9A-HJ-NP-Za-km-z]{93}$/, label: "Monero (4…)" },
  monero: { pattern: /^4[0-9AB][1-9A-HJ-NP-Za-km-z]{93}$/, label: "Monero (4…)" },

  // ── Algorand ─────────────────────────────────────────────────────────────────
  algo: { pattern: /^[A-Z2-7]{58}$/, label: "Algorand (A–Z / 2–7, 58 chars)" },
  algorand: { pattern: /^[A-Z2-7]{58}$/, label: "Algorand" },

  // ── NEAR ─────────────────────────────────────────────────────────────────────
  near: { pattern: /^[a-z0-9_-]{2,64}(\.near)?$/, label: "NEAR (account.near or hex)" },

  // ── Aptos / Sui ──────────────────────────────────────────────────────────────
  apt: { pattern: /^0x[0-9a-fA-F]{1,64}$/, label: "Aptos (0x…)" },
  aptos: { pattern: /^0x[0-9a-fA-F]{1,64}$/, label: "Aptos (0x…)" },
  sui: { pattern: /^0x[0-9a-fA-F]{64}$/, label: "Sui (0x + 64 hex)" },

  // ── Hedera ───────────────────────────────────────────────────────────────────
  hbar: { pattern: /^0\.0\.[0-9]+$/, label: "Hedera (0.0.XXXXX)" },
  hedera: { pattern: /^0\.0\.[0-9]+$/, label: "Hedera (0.0.XXXXX)" },
};

/**
 * Returns a fallback ChainEntry by checking chainType → networkKey → coinSymbol → chainName
 * (all lowercased, longest match wins to avoid false-positive on short keys like "eth").
 *
 * Real seeded networkKeys don't consistently put the chain token first — e.g.
 * "bnb_bsc", "matic_polygon" (prefix-style) vs "usdt_erc20", "usdt_bsc",
 * "usdt_trc20" (suffix-style, coin symbol comes first). A prefix-only match
 * silently misses every suffix-style key — this is exactly how a BNB Smart
 * Chain (bnb_bsc) network fell through to the generic fallback and accepted
 * a TRON-shaped address. Match anywhere in the string instead.
 */
function lookup(key) {
  const k = key.toLowerCase().replace(/[-_\s]/g, "");
  if (CHAIN_MAP[k]) return CHAIN_MAP[k];
  let best = null;
  let bestLen = 0;
  for (const [mapKey, entry] of Object.entries(CHAIN_MAP)) {
    if (k.includes(mapKey) && mapKey.length > bestLen) {
      best = entry;
      bestLen = mapKey.length;
    }
  }
  return best;
}

// chainType is only ever one of the 3 coarse CoinNetworkEntry enum values
// ('ACCOUNT_BASED' | 'UTXO' | 'TAG_MEMO') — never a real chain identifier.
// It must NOT go through the fuzzy lookup() above: stripped, 'ACCOUNT_BASED'
// becomes "accountbased", which literally *contains* the "base" chain key
// (Base L2) as a substring. Since chainType is checked first, that false
// match silently forced EVERY network on every coin to validate as EVM,
// regardless of its real networkKey — this is exactly the bug where a Tron
// network started accepting 0x-prefixed addresses. Exact-match only.
function lookupExact(key) {
  const k = key.toLowerCase().replace(/[-_\s]/g, "");
  return CHAIN_MAP[k] || null;
}

export function getChainAddressValidation(chainType, networkKey, coinSymbol, chainName) {
  return (
    (chainType && lookupExact(chainType)) ||
    (networkKey && lookup(networkKey)) ||
    (coinSymbol && lookup(coinSymbol)) ||
    (chainName && lookup(chainName)) ||
    null
  );
}

// Validates `address` against `network` (expects networkKey/chainType/chainName/addressRegex)
// and `coinSymbol`. Returns null when valid, or an error message string when invalid.
export function validateWithdrawAddress(address, network, coinSymbol) {
  const trimmed = (address || "").trim();
  if (!trimmed) return "Enter a destination address";

  if (network?.addressRegex) {
    try {
      if (!new RegExp(network.addressRegex).test(trimmed)) {
        return "Address format does not look right for this network";
      }
      return null;
    } catch {
      // Malformed regex in config — fall through to the CHAIN_MAP fallback.
    }
  }

  const fallback = getChainAddressValidation(
    network?.chainType,
    network?.networkKey,
    coinSymbol,
    network?.chainName,
  );
  if (fallback && !fallback.pattern.test(trimmed)) {
    return `Invalid address for ${network?.chainName || "this network"} — expected ${fallback.label}`;
  }
  return null;
}
